import React, { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadPhotos, uploadFile } from '../../firebase/storageService';
import { COLORS } from '../../styles/tokens';
import { hasWeightDeviation } from '../../utils/carrierOrder';

export default function PickupStep({ order }) {
  const [pickupPhotos,  setPickupPhotos]  = useState([]);
  const [weightDoc,     setWeightDoc]     = useState(null);
  const [actualTons,    setActualTons]    = useState('');
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [previews,      setPreviews]      = useState([]);

  const [deviationThresholdPct,  setDeviationThresholdPct]  = useState(5);
  const [deviationMinWeightTons, setDeviationMinWeightTons] = useState(0);

  useEffect(() => {
    getDoc(doc(db, 'settings', 'platform'))
      .then(snap => {
        if (!snap.exists()) return;
        const data = snap.data();
        if (typeof data.deviationThresholdPct  === 'number') setDeviationThresholdPct(data.deviationThresholdPct);
        if (typeof data.deviationMinWeightTons === 'number') setDeviationMinWeightTons(data.deviationMinWeightTons);
      })
      .catch(() => {}); // fail silently — defaults remain
  }, []);

  function handlePickupPhotos(e) {
    const files = Array.from(e.target.files).slice(0, 5);
    setPickupPhotos(files);
    setPreviews(files.map(f => URL.createObjectURL(f)));
  }

  function validate() {
    // Vekt er valgfri for timeoppdrag og ordre uten bestilt mengde; oppgis den, må den være over 0
    if (!weightRequired && String(actualTons).trim() === '') return null;
    if (!actualTons) return 'Registrer faktisk vekt';
    if (!(Number(actualTons) > 0)) return weightRequired ? 'Registrer faktisk vekt' : 'Faktisk vekt må være over 0';
    return null;
  }

  // Faktisk vekt kreves bare når oppdraget har bestilt mengde og ikke er timeoppdrag
  const weightRequired = !order.hourly && order.estimatedTons != null;

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    setLoading(true);
    setError('');

    try {
      const tons = String(actualTons).trim() === '' ? null : Number(actualTons); // null når vekt ikke er oppgitt (valgfri)

      // Last opp bilder til Firebase Storage
      const [pickupUrls, weightDocUrl] = await Promise.all([
        pickupPhotos.length > 0 ? uploadPhotos(pickupPhotos, `deliveries/${order.id}/pickup`) : Promise.resolve([]),
        weightDoc ? uploadFile(weightDoc, `deliveries/${order.id}/weight/${Date.now()}.${weightDoc.name.split('.').pop()}`) : Promise.resolve(''),
      ]);

      // Skip if the order's estimated weight is below the minimum threshold
      // (og for timeoppdrag eller ordre uten bestilt mengde: ingen sammenligning)
      const hasDeviation = tons != null && hasWeightDeviation(order, tons, { thresholdPct: deviationThresholdPct, minWeightTons: deviationMinWeightTons });

      // Beregn vektavvik og prisjustering (basert på fastpris eller prisforslag) bare når det finnes et avvik
      let deviationTons = 0, deviationPct = 0, priceAdjustment = 0;
      if (hasDeviation) {
        deviationTons = tons - order.estimatedTons;
        deviationPct  = Math.abs(deviationTons) / order.estimatedTons;
        const basePrice   = order.fixedPrice ?? order.suggestedPrice ?? 0;
        const pricePerTon = basePrice / order.estimatedTons;
        priceAdjustment   = Math.round(deviationTons * pricePerTon);
      }

      const updateData = {
        status:            'in_progress',
        pickupPhotoUrls:   pickupUrls,
        weightDocumentUrl: weightDocUrl,
        pickupActualTons:  tons,
        pickupRegisteredAt: serverTimestamp(),
      };

      if (hasDeviation) {
        updateData.pendingAdjustment = {
          actualTons:       tons,
          estimatedTons:    order.estimatedTons,
          deviationTons,
          deviationPct:     Math.round(deviationPct * 100),
          priceAdjustment,
          approvedByOrderer: false,
        };
      }

      await updateDoc(doc(db, 'orders', order.id), updateData);
    } catch (err) {
      console.error(err);
      setError('Opplasting feilet. Sjekk internettforbindelsen og prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  const tons = Number(actualTons);
  // Sammenligning mot bestilt mengde gjelder ikke timeoppdrag og ordre uten bestilt mengde
  const compareToOrdered = order.estimatedTons > 0 && !order.hourly;
  const deviationPct = tons && compareToOrdered
    ? Math.abs(tons - order.estimatedTons) / order.estimatedTons
    : 0;
  const hasDeviation = hasWeightDeviation(order, tons, { thresholdPct: deviationThresholdPct, minWeightTons: deviationMinWeightTons });

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <SectionTitle>1. Foto av lasten</SectionTitle>
      <p style={styles.hint}>Valgfritt, maks. 5 bilder</p>

      <label style={styles.fileLabel}>
        📷 Velg bilder
        <input
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          onChange={handlePickupPhotos}
          style={{ display: 'none' }}
        />
      </label>

      {previews.length > 0 && (
        <div style={styles.previews}>
          {previews.map((src, i) => (
            <img key={i} src={src} alt={`Bilde ${i + 1}`} style={styles.preview} />
          ))}
        </div>
      )}

      <SectionTitle>2. Faktisk vekt{weightRequired ? '' : ' (valgfritt)'}</SectionTitle>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          style={{ ...styles.input, width: 140 }}
          type="number"
          step="0.1"
          min="0.1"
          placeholder="0.0"
          value={actualTons}
          onChange={e => setActualTons(e.target.value)}
        />
        <span style={{ fontSize: 15, color: COLORS.textMid }}>
          tonn{order.estimatedTons != null && ` (estimert: ${order.estimatedTons} t)`}
        </span>
      </div>

      {tons > 0 && compareToOrdered && (
        <div style={{
          ...styles.deviationBox,
          background: hasDeviation ? COLORS.warningBg : COLORS.successBg,
          border: `1px solid ${hasDeviation ? COLORS.warningBorder : COLORS.successBorder}`,
        }}>
          {hasDeviation ? (
            <>
              ⚠️ Avvik på <strong>{Math.round(deviationPct * 100)}%</strong>
              {' '}({deviationTonsText(tons - order.estimatedTons)} tonn)
              — bestiller vil bli varslet og må godkjenne prisjustering
            </>
          ) : (
            <>✓ Innenfor tillatt avvik ({'<'}{deviationThresholdPct}%)</>
          )}
        </div>
      )}

      <SectionTitle>3. Foto av vektseddel</SectionTitle>
      <label style={styles.fileLabel}>
        📄 Last opp vektseddel
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={e => setWeightDoc(e.target.files[0])}
          style={{ display: 'none' }}
        />
      </label>
      {weightDoc && <p style={styles.fileConfirm}>✓ {weightDoc.name}</p>}

      {error && <p style={styles.error}>{error}</p>}

      <button type="submit" style={styles.btn} disabled={loading}>
        {loading ? 'Laster opp…' : '🚛 Registrer henting'}
      </button>
    </form>
  );
}

function deviationTonsText(diff) {
  return (diff > 0 ? '+' : '') + diff.toFixed(1);
}

function SectionTitle({ children }) {
  return <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.textDark, margin: '16px 0 6px' }}>{children}</h3>;
}

const styles = {
  form:        { display: 'flex', flexDirection: 'column', gap: 8 },
  hint:        { fontSize: 13, color: COLORS.textLight, margin: 0 },
  fileLabel:   { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 8, border: `2px dashed ${COLORS.border}`, cursor: 'pointer', fontSize: 14, color: COLORS.textMid, background: COLORS.surfaceHover, width: 'fit-content' },
  previews:    { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 },
  preview:     { width: 80, height: 80, objectFit: 'cover', borderRadius: 6 },
  input:       { padding: '10px 12px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 15 },
  deviationBox:{ padding: '10px 14px', borderRadius: 6, fontSize: 13, marginTop: 4 },
  fileConfirm: { fontSize: 13, color: COLORS.success, margin: '4px 0 0' },
  error:       { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 12px', borderRadius: 6, fontSize: 14 },
  btn:         { height: 54, borderRadius: 27, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 12 },
};
